import { AdminButton } from "@admin/components/ui/admin-button";
import {
  canUsePhoneVerificationReset,
  PHONE_VERIFICATION_RESET_PATH,
} from "@admin/pages/test-support/phoneVerificationAccess";
import LogoSVG from "@svg/logo-ko-white.svg?react";
import LogoutSVG from "@svg/logout.svg?react";
import { cn } from "@utils/common";
import { toastError, toastSuccess } from "@utils/toast";
import { ChevronDown, KeyRound, MoreHorizontal } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import ConfirmDialog from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAdminChangeMyPassword, useAdminFindMyPermissions } from "@/hooks/admin/adminAdminsHooks";
import { useAdminLogout } from "@/hooks/admin/adminAuthHooks";
import { useBankStatus } from "@/hooks/admin/adminBankHooks";
import { usePartnerAdminLogout } from "@/hooks/partner-admin/adminAuthHooks";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { canAccess, getRouteAccess } from "@/libs/routeAccess";

interface AdminSidebarNavProps extends React.HTMLAttributes<HTMLElement> {}

// 단일 메뉴 아이템 타입
type NavItem = {
  title: string;
  href: string;
  icon?: React.ReactNode;
  badge?: number;
  children?: {
    title: string;
    href: string;
  }[];
};

// 그룹 메뉴 타입
type NavGroup = {
  groupTitle: string;
  items: NavItem[];
};

// 사이드바 아이템 유니온 타입
type SidebarItem = NavItem | NavGroup;

/** 문제은행 — 로컬·스테이징에만 있어서, 서버가 있다고 답할 때만 메뉴를 보인다 */
const BANK_PATH = "/bank";

// 타입 가드 함수
function isNavGroup(item: SidebarItem): item is NavGroup {
  return "groupTitle" in item && "items" in item;
}

const sidebarItems: SidebarItem[] = [
  // 분석 그룹
  {
    groupTitle: "분석",
    items: [
      {
        title: "대시보드",
        href: "/dashboard",
      },
      {
        title: "사용자 지표",
        href: "/statistics",
      },
      {
        title: "세그먼트 분석",
        href: "/segment-analysis",
      },
      {
        title: "마케팅 대시보드",
        href: "/marketing-dashboard",
      },
      {
        title: "획득 리포트",
        href: "/acquisition-report",
      },
      {
        title: "쿠팡 파트너스 수익",
        href: "/coupang-partners",
      },
    ],
  },

  // 사용자 그룹
  {
    groupTitle: "사용자",
    items: [
      {
        title: "회원",
        href: "/users",
      },
      {
        title: "지급 요청",
        href: "/grant-requests",
      },
    ],
  },

  // 컨텐츠 그룹
  {
    groupTitle: "컨텐츠",
    items: [
      {
        title: "퀴즈팩",
        href: "/quiz-packs/list",
      },
      {
        title: "배틀",
        href: "/battles",
      },
      {
        title: "커리큘럼",
        href: "/curriculums",
      },
      {
        title: "영단어 관리",
        href: "/vocab-words",
      },
      {
        title: "일본어 검수",
        href: "/vocab-ja-review",
      },
      {
        title: "문제은행",
        href: BANK_PATH,
      },
      {
        title: "리워드 이벤트",
        href: "/reward-events",
      },
      {
        title: "프로모션 배너",
        href: "/promo-banners",
      },
    ],
  },

  // 광고 시스템 그룹
  {
    groupTitle: "광고 관리",
    items: [
      {
        title: "대시보드",
        href: "/ad-dashboard",
      },
      {
        title: "광고주",
        href: "/ad-advertisers",
      },
      {
        title: "캠페인",
        href: "/ad-campaigns",
      },
      {
        title: "광고그룹",
        href: "/ad-groups",
      },
      {
        title: "광고소재",
        href: "/ad-ads",
      },
    ],
  },

  // 고객 지원 그룹
  {
    groupTitle: "고객 지원",
    items: [
      {
        title: "신고",
        href: "/quiz-packs/report",
      },
      {
        title: "공지사항",
        href: "/notices",
      },
      {
        title: "FAQ",
        href: "/faqs",
      },
    ],
  },

  // 스터디그룹
  {
    groupTitle: "스터디그룹",
    items: [
      {
        title: "Space 관리",
        href: "/study-groups",
      },
    ],
  },

  // 기부 관리 그룹
  {
    groupTitle: "기부 관리",
    items: [
      {
        title: "단체 관리",
        href: "/donation-organizations",
      },
      {
        title: "캠페인 관리",
        href: "/donation-campaigns",
      },
    ],
  },

  // 기프티콘 그룹
  {
    groupTitle: "기프티콘",
    items: [
      {
        title: "주문 내역",
        href: "/giftcon/orders",
      },
      {
        title: "브랜드 관리",
        href: "/giftcon/brands",
      },
    ],
  },

  // 단축 URL 그룹
  {
    groupTitle: "단축 URL",
    items: [
      {
        title: "대시보드",
        href: "/short-urls",
      },
      {
        title: "URL 목록",
        href: "/short-urls/list",
      },
    ],
  },

  // 설정 그룹
  {
    groupTitle: "설정",
    items: [
      {
        title: "관리자",
        href: "/admins",
      },
      {
        title: "개발 대시보드",
        href: "/dev-dashboard",
      },
      {
        title: "전역 설정",
        href: "/global-settings",
      },
      {
        title: "파트너 API 키",
        href: "/partner-api-keys",
      },
      {
        title: "핀크럭스 연동",
        href: "/partner-pincrux",
      },
      {
        title: "연속출석 테스트 지원",
        href: "/test-support/streak",
      },
      {
        title: "본인인증 초기화",
        href: PHONE_VERIFICATION_RESET_PATH,
      },
      {
        title: "버전 관리",
        href: "/versions",
      },
    ],
  },

  // 약관/문서 — 시스템 설정과 별개의 법무·운영 문서 관리 그룹
  {
    groupTitle: "문서",
    items: [
      {
        title: "약관/문서 관리",
        href: "/service-documents",
      },
    ],
  },

];

// 아코디언 height transition 컴포넌트
function AccordionContent({
  isExpanded,
  children,
}: {
  isExpanded: boolean;
  children: React.ReactNode;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(
    isExpanded ? undefined : 0,
  );

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    if (isExpanded) {
      const scrollHeight = el.scrollHeight;
      setHeight(scrollHeight);
      const timer = setTimeout(() => setHeight(undefined), 200);
      return () => clearTimeout(timer);
    } else {
      setHeight(el.scrollHeight);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setHeight(0));
      });
    }
  }, [isExpanded]);

  return (
    <div
      ref={contentRef}
      className="overflow-hidden transition-[height] duration-200 ease-in-out"
      style={{ height: height != null ? `${height}px` : "auto" }}
    >
      {children}
    </div>
  );
}

export default function AdminSidebarNav({ ...props }: AdminSidebarNavProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const logout = useAdminLogout();
  const partnerLogout = usePartnerAdminLogout();
  const { adminRole, adminEmail, backendRole } = useAdminAuth();
  const { data: myPermissions } = useAdminFindMyPermissions();
  const changePassword = useAdminChangeMyPassword();
  // 그룹이 활성 페이지를 포함하는지 확인
  const isGroupActive = useCallback(
    (group: NavGroup) =>
      group.items.some(
        (item) =>
          pathname.includes(item.href) ||
          item.children?.some((child) => pathname.includes(child.href)),
      ),
    [pathname],
  );

  // 현재 활성 페이지가 속한 그룹 초기 펼침, 없으면 "사용자"
  const getInitialExpanded = useCallback((): string => {
    for (const item of sidebarItems) {
      if (isNavGroup(item) && isGroupActive(item)) {
        return item.groupTitle;
      }
    }
    return "사용자";
  }, [isGroupActive]);

  const [expandedGroup, setExpandedGroup] = useState<string | null>(getInitialExpanded);

  // 페이지 변경 시 활성 그룹 자동 펼침
  useEffect(() => {
    for (const item of sidebarItems) {
      if (isNavGroup(item) && isGroupActive(item)) {
        setExpandedGroup(item.groupTitle);
        return;
      }
    }
  }, [pathname, isGroupActive]);

  const toggleGroup = (groupTitle: string) => {
    setExpandedGroup((prev) => (prev === groupTitle ? null : groupTitle));
  };

  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleLogout = () => {
    if (adminRole === "PARTNER_ADMIN") {
      partnerLogout();
    } else {
      logout();
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toastError("모든 필드를 입력해주세요.");
      return;
    }
    if (newPassword.length < 8) {
      toastError("새 비밀번호는 최소 8자 이상이어야 합니다.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toastError("새 비밀번호가 일치하지 않습니다.");
      return;
    }
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      toastSuccess("비밀번호가 변경되었습니다.");
      setIsPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch {
      toastError("비밀번호 변경에 실패했습니다. 현재 비밀번호를 확인해주세요.");
    }
  };

  // 역할·권한 체크 — 경로별 요구사항은 라우트 가드와 공유하는 routeAccess 매핑 사용
  const canSeeBank = canAccess(getRouteAccess(BANK_PATH), myPermissions ?? [], backendRole);
  const { data: bankStatus } = useBankStatus(adminRole !== "PARTNER_ADMIN" && canSeeBank);
  const canAccessItem = (item: NavItem) =>
    canAccess(getRouteAccess(item.href), myPermissions ?? [], backendRole) &&
    (item.href !== BANK_PATH || !!bankStatus?.available) &&
    (item.href !== PHONE_VERIFICATION_RESET_PATH || canUsePhoneVerificationReset(adminEmail));

  // 역할·권한에 따라 sidebarItems 필터링
  const filteredSidebarItems = sidebarItems.filter((item) => {
    if (isNavGroup(item)) {
      return item.items.some((navItem) => canAccessItem(navItem));
    }
    return canAccessItem(item);
  });

  // 역할에 따른 표시명
  const roleDisplayName =
    adminRole === "PARTNER_ADMIN"
      ? "스웨치 매체사 관리자"
      : backendRole === "STAFF"
        ? "스웨치 스태프"
        : "스웨치 총관리자";

  // 단일 NavItem 렌더링 함수
  const renderNavItem = (item: NavItem, isNested: boolean = false) => {
    const isActive =
      pathname.includes(item.href) ||
      (item.children &&
        item.children.some((child) => pathname.includes(child.href)));

    return (
      <React.Fragment key={item.href}>
        <Link
          to={item.href}
          className={cn(
            "group flex cursor-pointer items-center space-x-2 py-3 font-normal text-white transition-transform hover:border-r-4 hover:border-[#00CCCC] hover:bg-gray-700 hover:font-semibold active:scale-95",
            isNested ? "pl-8 pr-4 text-sm" : "pl-4 pr-4",
            isActive &&
              "border-r-4 border-[#00CCCC] bg-gray-700 fill-[#00CCCC] font-semibold"
          )}
        >
          {!isNested && item.icon && (
            <div
              className={cn(
                "pr-2 text-white group-hover:text-[#00CCCC]",
                isActive && "text-[#00CCCC]"
              )}
            >
              {item.icon}
            </div>
          )}
          <span className="flex-1">{item.title}</span>
          {item.badge != null && (
            <span
              className={cn(
                "ml-2 inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                item.badge > 0
                  ? "bg-[#00CCCC] text-gray-900"
                  : "bg-gray-600 text-gray-300"
              )}
            >
              {item.badge}
            </span>
          )}
        </Link>

        {/* 하위 메뉴 (children) */}
        {item.children && (
          <div className={cn("flex flex-col", isNested ? "pl-14" : "pl-7")}>
            {item.children.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                className={cn(
                  "w-full border-l border-[#8D95A1] px-6 py-2 text-[15px] text-sm font-normal text-[#8D95A1] hover:font-semibold hover:text-white",
                  pathname.includes(child.href) && "font-semibold text-white"
                )}
              >
                {child.title}
              </Link>
            ))}
          </div>
        )}
      </React.Fragment>
    );
  };

  // NavGroup 아코디언 렌더링 함수
  const renderNavGroup = (group: NavGroup, isFirst: boolean = false) => {
    const visibleItems = group.items.filter((item) => canAccessItem(item));
    const isExpanded = expandedGroup === group.groupTitle;

    return (
      <div key={group.groupTitle} className={isFirst ? "" : "mt-2"}>
        {/* 그룹 타이틀 — 클릭으로 토글 */}
        <button
          type="button"
          onClick={() => toggleGroup(group.groupTitle)}
          className="flex w-full cursor-pointer items-center justify-between px-4 py-2 transition-colors hover:bg-gray-700/50 active:scale-95"
        >
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
            {group.groupTitle}
          </span>
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 text-gray-500 transition-transform duration-200",
              !isExpanded && "-rotate-90",
            )}
          />
        </button>

        {/* 그룹 내 아이템들 — 아코디언 */}
        <AccordionContent isExpanded={isExpanded}>
          {visibleItems.map((item) => renderNavItem(item, true))}
        </AccordionContent>
      </div>
    );
  };

  return (
    <div className="sticky left-0 top-0 z-30 flex h-screen w-[230px] shrink-0 flex-col space-y-6 bg-gray-800 pt-5">
      <Link to="/dashboard" className="flex items-center justify-center">
        <LogoSVG className="h-6 w-auto" />
      </Link>

      <nav className="flex h-full flex-col overflow-y-auto" {...props}>
        {filteredSidebarItems.map((item, index) =>
          isNavGroup(item) ? renderNavGroup(item, index === 0) : renderNavItem(item)
        )}

        <div className="flex-1" />

        {/* 하단 영역 */}
        <div className="border-t border-gray-700">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-white">{adminEmail}</p>
              <p className="text-xs text-gray-400">{roleDisplayName}</p>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-white">
                  <MoreHorizontal className="size-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="end"
                className="w-40 p-1"
              >
                <button
                  onClick={() => setIsPasswordDialogOpen(true)}
                  className="flex w-full items-center space-x-2 rounded px-3 py-2 text-sm hover:bg-gray-100"
                >
                  <KeyRound className="size-4" />
                  <span>비밀번호 변경</span>
                </button>
                <button
                  onClick={() => setIsLogoutDialogOpen(true)}
                  className="flex w-full items-center space-x-2 rounded px-3 py-2 text-sm hover:bg-gray-100"
                >
                  <LogoutSVG className="size-4 stroke-current" />
                  <span>로그아웃</span>
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </nav>

      <ConfirmDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title="로그아웃"
        description="정말 로그아웃 하시겠습니까?"
        confirmText="로그아웃"
        cancelText="취소"
        onConfirm={handleLogout}
      />

      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="rounded-lg">
          <DialogHeader>
            <DialogTitle>비밀번호 변경</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="mb-1 block text-sm text-content-tertiary">
                현재 비밀번호
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="현재 비밀번호를 입력하세요"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-content-tertiary">
                새 비밀번호
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="새 비밀번호를 입력하세요 (최소 8자)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-content-tertiary">
                새 비밀번호 확인
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="새 비밀번호를 다시 입력하세요"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <AdminButton
              variant="outline"
              size="md"
              onClick={() => setIsPasswordDialogOpen(false)}
            >
              취소
            </AdminButton>
            <AdminButton
              size="md"
              onClick={handleChangePassword}
              disabled={changePassword.isPending}
            >
              {changePassword.isPending ? "변경 중..." : "변경"}
            </AdminButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
